import { useState } from 'react';
import {
  useBusinessLinesAdmin,
  useCreateAssignment,
  useDeleteAssignment,
  useMe,
} from '@/lib/queries';
import type { BusinessLineView, ManagerView, UserSearchHit } from '@/lib/types';
import { AppAdminsCard } from '@/components/AppAdminsCard';
import { Avatar } from '@/components/Avatar';
import { CsvImportCard } from '@/components/CsvImportCard';
import { DocsCard } from '@/components/DocsCard';
import { UserMenu } from '@/components/UserMenu';
import { UserPicker } from '@/components/UserPicker';

interface Props {
  onNavigate: (view: 'dashboard' | 'config') => void;
}

function ManagerChip({
  m,
  onRemove,
  removing,
}: {
  m: ManagerView;
  onRemove: () => void;
  removing: boolean;
}) {
  return (
    <div className="group flex items-center gap-2 border border-ink-800 bg-ink-900/60 py-1 pl-1 pr-2">
      <Avatar upn={m.upn} fullName={m.display_name} size="sm" />
      <div className="min-w-0">
        <div className="truncate text-sm text-ink-100">{m.display_name}</div>
        <div className="flex items-center gap-2 text-[0.65rem] text-ink-500">
          <span className="truncate">{m.upn}</span>
          {!m.in_managers_group ? (
            <span
              title="No está en el grupo Copilot-Managers — no podrá iniciar sesión"
              className="text-amber-400"
            >
              ⚠
            </span>
          ) : null}
        </div>
      </div>
      <button
        type="button"
        disabled={removing}
        onClick={onRemove}
        title="Quitar"
        className="ml-2 grid h-6 w-6 place-items-center text-ink-500 transition-colors hover:bg-ink-800 hover:text-ink-100 disabled:opacity-50"
      >
        <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden="true">
          <path
            d="M1 1l12 12M13 1L1 13"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      </button>
    </div>
  );
}

function BusinessLineRow({
  bl,
  onAdd,
  onRemove,
  pendingRemoveKey,
}: {
  bl: BusinessLineView;
  onAdd: (bl: string) => void;
  onRemove: (m: ManagerView, bl: string) => void;
  pendingRemoveKey: string | null;
}) {
  return (
    <tr className="border-b border-ink-800/60 align-top">
      <td className="px-6 py-5">
        <div className="text-base text-ink-100">{bl.business_line}</div>
        <div className="mt-0.5 text-xs text-ink-500 tabular-nums">
          {bl.employees} empleados
        </div>
      </td>
      <td className="px-6 py-5">
        {bl.managers.length === 0 ? (
          <div className="text-sm text-ink-600">Sin manager asignado</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {bl.managers.map((m) => (
              <ManagerChip
                key={m.user_oid}
                m={m}
                onRemove={() => onRemove(m, bl.business_line)}
                removing={
                  pendingRemoveKey === `${m.user_oid}:${bl.business_line}`
                }
              />
            ))}
          </div>
        )}
      </td>
      <td className="w-32 px-6 py-5 text-right">
        <button
          type="button"
          onClick={() => onAdd(bl.business_line)}
          className="border border-ink-700 px-3 py-1.5 text-xs text-ink-200 transition-colors hover:bg-ink-800"
        >
          + Asignar
        </button>
      </td>
    </tr>
  );
}

export function Config({ onNavigate }: Props) {
  const me = useMe();
  const { data, isLoading } = useBusinessLinesAdmin(true);
  const createMut = useCreateAssignment();
  const deleteMut = useDeleteAssignment();
  const [pickerFor, setPickerFor] = useState<string | null>(null);

  if (me.data && !me.data.is_app_admin) {
    return (
      <main className="grid min-h-screen place-items-center px-6">
        <div className="text-center text-ink-500">
          <div className="label">Sin permisos</div>
          <p className="mt-2 text-sm">Esta sección está reservada para administradores.</p>
          <button
            type="button"
            onClick={() => onNavigate('dashboard')}
            className="mt-6 border border-ink-700 px-4 py-2 text-xs text-ink-200 hover:bg-ink-900"
          >
            Volver al dashboard
          </button>
        </div>
      </main>
    );
  }

  const excludeForPicker = pickerFor
    ? (data?.items.find((b) => b.business_line === pickerFor)?.managers.map(
        (m) => m.user_oid,
      ) ?? [])
    : [];

  return (
    <main className="mx-auto max-w-5xl px-6 py-10">
      <header className="mb-10 flex items-center justify-between border-b border-ink-800 pb-6">
        <div className="flex items-baseline gap-4">
          <button
            type="button"
            onClick={() => onNavigate('dashboard')}
            className="text-xs text-ink-500 hover:text-ink-200"
          >
            ← Dashboard
          </button>
          <div>
            <div className="label">Configuración</div>
            <h1 className="mt-1 text-2xl font-light tracking-tight text-ink-50">
              Empleados y managers
            </h1>
          </div>
        </div>
        <UserMenu me={me.data} onNavigate={onNavigate} />
      </header>

      <div className="mb-10">
        <CsvImportCard />
      </div>

      <div className="mb-10">
        <AppAdminsCard />
      </div>

      <div className="mb-3">
        <div className="label">Acceso</div>
        <h2 className="mt-1 text-lg font-light text-ink-50">Managers por filial</h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-500">
          Al asignar un manager podrá entrar al dashboard y ver únicamente los datos de su(s)
          filial(es).
        </p>
      </div>

      <section className="panel">
        {isLoading || !data ? (
          <div className="py-20 text-center text-ink-500">Cargando…</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-800 text-left text-[0.65rem] uppercase tracking-widest text-ink-500">
                <th className="px-6 py-3 font-normal">Filial</th>
                <th className="px-6 py-3 font-normal">Managers</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((bl) => (
                <BusinessLineRow
                  key={bl.business_line}
                  bl={bl}
                  onAdd={(line) => setPickerFor(line)}
                  onRemove={(m, line) =>
                    deleteMut.mutate({ user_oid: m.user_oid, business_line: line })
                  }
                  pendingRemoveKey={
                    deleteMut.isPending && deleteMut.variables
                      ? `${deleteMut.variables.user_oid}:${deleteMut.variables.business_line}`
                      : null
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div className="mt-10">
        <DocsCard editable={true} />
      </div>

      <UserPicker
        open={pickerFor !== null}
        title={pickerFor ? `Manager para ${pickerFor}` : ''}
        excludeOids={excludeForPicker}
        onClose={() => setPickerFor(null)}
        onPick={(u: UserSearchHit) => {
          if (!pickerFor) return;
          createMut.mutate(
            { user_oid: u.oid, business_line: pickerFor },
            { onSuccess: () => setPickerFor(null) },
          );
        }}
      />
    </main>
  );
}
